/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Card, Col, Form, Input, Row, Spin, Typography } from 'antd';
import { CheckCircleOutlined, LoadingOutlined, LockOutlined } from '@ant-design/icons';
import GlobalFooter from '../components/Layout/GlobalFooter';
import DocumentTitle from '../components/DocumentTitle';

// Misc imports
import AppLogo from '../assets/logo-full.svg';
import { NAV_LOGIN } from '../constants/navigation';
import { useResetPasswordMutation } from '../store/forgotPassword/forgotPasswordApi';
import openConsoleAlertMessage from '../utils/misc/openConsoleAlertMessage';

// Constant declarations
const baseClassName = 'login-screen';
const { Text } = Typography;

const PasswordResetScreen = () => {
  const { token: paramsToken } = useParams();
  const token = new URLSearchParams(window.location.search).get('token') || paramsToken;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [submitReset, setSubmitReset] = useState(false);
  const [resetPassword, { isLoading: isResettingPassword, isSuccess: isPasswordReset, reset: resetMutation }] = useResetPasswordMutation();

  const onFinish = async (values) => {
    let newValues = {
      password: values.newPassword,
      token
    };

    await resetPassword({ data: newValues });
    resetMutation();
    setSubmitReset(true);
  };

  const handleError = () => {
    setSubmitReset(false);
    openConsoleAlertMessage({ type: 'error', message: t('Something went wrong while resetting your password, please try again.') });
  };

  const renderAfterResetContent = () => {
    if (isResettingPassword) {
      return (
        <div className="text-center">
          <div>
            <Spin size="large" indicator={<LoadingOutlined className="fs-xxl" spin />} />
          </div>
          <div className="mt-5">
            <h3>{t('Please wait while your password is being reset...')}</h3>
          </div>
        </div>
      );
    } else {
      return (
        <Fragment>
          {
            isPasswordReset ? (
              <div className="text-center">
                <Text
                  className="fs-xxxxl mb-0"
                  type="success"
                >
                  <CheckCircleOutlined />
                </Text>

                <h2>{t('Your password has been reset successfully! You can now log in with your new password.')}</h2>

                <Button
                  type="primary"
                  size="large"
                  className="w-100"
                  onClick={() => navigate(NAV_LOGIN)}
                >
                  {t('Log in')}
                </Button>
              </div>
            ) : (handleError())
          }
        </Fragment>
      );
    }
  };

  const resetPasswordForm = () => {
    return (
      <Form
        layout="vertical"
        form={form}
        name="resetPasswordForm"
        onFinish={onFinish}
      >
        <Form.Item
          name="newPassword"
          label={t('New password')}
          colon={false}
          rules={[{
            required: true,
            validator: async (_rule, value) => {
              if (!value) {
                throw new Error(t('Please enter your new password.'));
              }
              if (value.length < 8) {
                throw new Error(t('Password must be at least 8 characters.'));
              }
            }
          }]}
        >
          <Input.Password placeholder={t('Please enter new password')} size="large" prefix={<LockOutlined className="text-secondary mr-2" />} />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={t('Confirm password')}
          colon={false}
          rules={[{
            required: true,
            validator: async (_rule, value) => {
              if (value !== form.getFieldValue('newPassword')) {
                throw new Error(t('Passwords do not match.'));
              }
            }
          }]}
        >
          <Input.Password placeholder={t('Please confirm new password')} size="large" prefix={<LockOutlined className="text-secondary mr-2" />} />
        </Form.Item>

        <Form.Item wrapperCol={{ span: 24 }}>
          <Button type="primary" htmlType="submit" size="large" block>
            {t('Submit')}
          </Button>
        </Form.Item>
      </Form>
    );
  };

  return (
    <div className={baseClassName}>
      <DocumentTitle title={t('Reset Password')} />

      <Row justify="center">
        <img src={AppLogo} alt="logo" width={200} className="pt-10 mt-10 mb-10" />
      </Row>

      <Row justify="center">
        <Col xs={24} sm={20} md={12} lg={12} xl={7}>
          <Card
            title={(
              <Row justify="center">
                {t('Reset Password')}
              </Row>
            )}
            bordered={false}
          >
            {submitReset ? renderAfterResetContent() : resetPasswordForm()}
          </Card>
        </Col>
      </Row>

      <Row justify="center">
        <Col>
          <GlobalFooter />
        </Col>
      </Row>
    </div>
  );
};

export default PasswordResetScreen;
